import { buildApiUrl, API_CONFIG } from '../config';
import { apiFetch } from '../utils/auth';

// Team API functions
export const teamAPI = {
  // Get all teams for an operation
  // Bei technischen Accounts kann eine supervisorID übergeben werden,
  // damit die API nach der echten Person filtert statt nach dem JWT-User
  getTeams: async (opsID: number, supervisorID?: number) => {
    let url = buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS(opsID));
    if (supervisorID) {
      url += `?supervisorID=${supervisorID}`;
      console.log('👤 getTeams: Filtere nach supervisorID (echte Person):', supervisorID);
    }
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Get all teams for an operation (by operation ID)
  getTeamsByOperation: async (orgID: number, opsID: number) => {
    console.log('🌐 API CALL: POST /api/v1/teams/by-operation', { orgID, opsID });
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_BY_OPERATION), {
      method: 'POST',
      body: JSON.stringify({ orgID, opsID }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Create new team
  createTeam: async (teamData: {
    opsID: number;
    name: string;
    task?: string;
    location?: string;
    startPressure?: number;
    radioChannel?: string;
    teamLeaderIndex?: number;
    creatorID: number;
    members?: Array<{
      firstName: string;
      lastName: string;
      pressure?: number;
    }>;
  }) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_CREATE), {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Update team
  updateTeam: async (teamId: number, teamData: any) => {
    console.log('🌐 API CALL: PUT /api/v1/teams/' + teamId, teamData);
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_UPDATE(teamId)), {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Delete team
  deleteTeam: async (teamId: number) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_DELETE(teamId)), {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Get team members
  getTeamMembers: async (teamId: number) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_MEMBERS(teamId)));
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Add member to team
  addTeamMember: async (teamId: number, memberData: {
    firstName: string;
    lastName: string;
    pressure?: number;
  }) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_MEMBERS_ADD(teamId)), {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Remove member from team
  removeTeamMember: async (teamId: number, memberId: number) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_MEMBERS_DELETE(teamId, memberId)), {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
};

// Settings API functions
export const settingsAPI = {
  // Normalize backend values to strict boolean
  normalizeQuickTruppEnabled: (value: any): boolean => {
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value === null || value === undefined) return false;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') return true;
      if (normalized === 'false' || normalized === '0' || normalized === '') return false;
    }
    return Boolean(value);
  },

  // Extract quick-trupp value from different backend response shapes
  extractQuickTruppEnabled: (payload: any): boolean => {
    const candidates = [
      payload?.enabled,
      payload?.quickTruppCreation,
      payload?.quick_trupp_creation,
      payload?.value,
      payload?.settingValue,
      payload?.data?.enabled,
      payload?.data?.quickTruppCreation,
      payload?.data?.quick_trupp_creation,
      payload?.data?.value,
      Array.isArray(payload) ? payload[0]?.enabled : undefined,
      Array.isArray(payload) ? payload[0]?.quickTruppCreation : undefined,
      Array.isArray(payload) ? payload[0]?.quick_trupp_creation : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.enabled : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.quickTruppCreation : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.quick_trupp_creation : undefined,
    ];

    for (const candidate of candidates) {
      if (candidate !== undefined) {
        return settingsAPI.normalizeQuickTruppEnabled(candidate);
      }
    }

    return false;
  },

  // Get quick trupp creation setting
  getQuickTruppCreation: async (orgID: number) => {
    const cacheBuster = Date.now();
    const url = buildApiUrl(`/api/v1/settings/quick-trupp-creation?orgID=${orgID}&_ts=${cacheBuster}`);
    console.log('🌐 API CALL: GET /api/v1/settings/quick-trupp-creation', { orgID, cacheBuster, url });
    const response = await apiFetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const enabled = settingsAPI.extractQuickTruppEnabled(data);
    console.log('✅ API RESPONSE: GET /api/v1/settings/quick-trupp-creation', {
      orgID,
      raw: data,
      extractedEnabled: enabled,
    });
    return {
      ...data,
      enabled,
    };
  },
  
  // Set quick trupp creation setting
  setQuickTruppCreation: async (orgID: number, enabled: boolean) => {
    console.log('🌐 API CALL: POST /api/v1/settings/quick-trupp-creation', { enabled, orgID });
    const response = await apiFetch(buildApiUrl('/api/v1/settings/quick-trupp-creation'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orgID, enabled }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    console.log('✅ API RESPONSE: POST /api/v1/settings/quick-trupp-creation', {
      orgID,
      requestedEnabled: enabled,
      raw: data,
    });
    return data;
  },

  normalizeTruppNamingMode: (value: any): 'leader' | 'number' => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'number') return 'number';
      if (normalized === 'leader') return 'leader';
    }
    return 'leader';
  },

  extractTruppNamingMode: (payload: any): 'leader' | 'number' => {
    const candidates = [
      payload?.mode,
      payload?.truppNamingMode,
      payload?.trupp_naming_mode,
      payload?.value,
      payload?.settingValue,
      payload?.data?.mode,
      payload?.data?.truppNamingMode,
      payload?.data?.trupp_naming_mode,
      payload?.data?.value,
      Array.isArray(payload) ? payload[0]?.mode : undefined,
      Array.isArray(payload) ? payload[0]?.value : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.mode : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.value : undefined,
    ];

    for (const candidate of candidates) {
      if (candidate !== undefined && candidate !== null) {
        return settingsAPI.normalizeTruppNamingMode(candidate);
      }
    }

    return 'leader';
  },

  getTruppNamingMode: async (orgID: number) => {
    const cacheBuster = Date.now();
    const url = buildApiUrl(`/api/v1/settings/trupp-naming-mode?orgID=${orgID}&_ts=${cacheBuster}`);
    console.log('🌐 API CALL: GET /api/v1/settings/trupp-naming-mode', { orgID, cacheBuster, url });
    const response = await apiFetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const mode = settingsAPI.extractTruppNamingMode(data);
    console.log('✅ API RESPONSE: GET /api/v1/settings/trupp-naming-mode', {
      orgID,
      raw: data,
      extractedMode: mode,
    });
    return {
      ...data,
      mode,
    };
  },

  setTruppNamingMode: async (orgID: number, mode: 'leader' | 'number') => {
    console.log('🌐 API CALL: POST /api/v1/settings/trupp-naming-mode', { orgID, mode });
    const response = await apiFetch(buildApiUrl('/api/v1/settings/trupp-naming-mode'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orgID, mode }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    console.log('✅ API RESPONSE: POST /api/v1/settings/trupp-naming-mode', {
      orgID,
      requestedMode: mode,
      raw: data,
    });
    return data;
  },

  getOrgShortName: async (orgID: number) => {
    const cacheBuster = Date.now();
    const url = buildApiUrl(`/api/v1/settings/org-short-name?orgID=${orgID}&_ts=${cacheBuster}`);
    const response = await apiFetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const shortName = String(data?.shortName ?? data?.value ?? '').trim();
    return { ...data, shortName };
  },

  setOrgShortName: async (orgID: number, shortName: string) => {
    const response = await apiFetch(buildApiUrl('/api/v1/settings/org-short-name'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID, shortName }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return { ...data, shortName: String(data?.shortName ?? shortName).trim() };
  },

  getTruppNamePrefix: async (orgID: number) => {
    const cacheBuster = Date.now();
    const url = buildApiUrl(`/api/v1/settings/trupp-name-prefix?orgID=${orgID}&_ts=${cacheBuster}`);
    const response = await apiFetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const enabled = data?.enabled === true || data?.enabled === 1 || data?.enabled === '1';
    return { ...data, enabled };
  },

  setTruppNamePrefix: async (orgID: number, enabled: boolean) => {
    const response = await apiFetch(buildApiUrl('/api/v1/settings/trupp-name-prefix'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID, enabled }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return {
      ...data,
      enabled: data?.enabled === true || data?.enabled === 1 || data?.enabled === '1' || enabled,
    };
  },

  applyTruppNamePrefix: (
    baseName: string,
    shortName: string | undefined | null,
    prefixEnabled: boolean
  ): string => {
    const base = String(baseName || '').trim();
    if (!base) return base;
    const prefix = String(shortName || '').trim();
    if (!prefixEnabled || !prefix) return base;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`^${escaped}\\s+`, 'i').test(base)) return base;
    return `${prefix} ${base}`;
  },

  extractTruppNumberFromName: (teamName: string | undefined): number | null => {
    if (!teamName) return null;
    // Accepts "Trupp 01" and "KONO Trupp 01"
    const match = /(?:^|\s)trupp\s+(\d+)$/i.exec(String(teamName).trim());
    if (!match) return null;
    const parsed = parseInt(match[1], 10);
    return Number.isNaN(parsed) ? null : parsed;
  },

  DEFAULT_STATUS_UPDATE_SCREENS: [
    { id: 'status', label: 'Status', enabled: true, order: 1 },
    { id: 'standort', label: 'Standort', enabled: true, order: 2 },
    { id: 'aufgabe', label: 'Aufgabe', enabled: true, order: 3 },
    { id: 'druck', label: 'Druck', enabled: true, order: 4 },
    { id: 'funkkanal', label: 'Funkkanal', enabled: true, order: 5 },
    { id: 'feedback', label: 'Feedback', enabled: true, order: 6 },
  ] as Array<{ id: string; label: string; enabled: boolean; order: number }>,

  normalizeStatusUpdateScreens: (input: any): Array<{ id: string; label: string; enabled: boolean; order: number }> => {
    const defaults = settingsAPI.DEFAULT_STATUS_UPDATE_SCREENS;
    let parsed = input;
    if (typeof input === 'string') {
      try { parsed = JSON.parse(input); } catch { return defaults.map((s) => ({ ...s })); }
    }
    if (!Array.isArray(parsed)) return defaults.map((s) => ({ ...s }));

    const knownIds = new Set(defaults.map((s) => s.id));
    const byId = new Map<string, { id: string; label: string; enabled: boolean; order: number }>();
    parsed.forEach((item: any, index: number) => {
      const id = String(item?.id || '').trim().toLowerCase();
      if (!knownIds.has(id) || byId.has(id)) return;
      const def = defaults.find((s) => s.id === id)!;
      byId.set(id, {
        id,
        label: def.label,
        enabled: !(item.enabled === false || item.enabled === 0 || item.enabled === '0'),
        order: Number.isFinite(Number(item.order)) ? Number(item.order) : index + 1,
      });
    });
    defaults.forEach((screen, index) => {
      if (!byId.has(screen.id)) byId.set(screen.id, { ...screen, order: 100 + index });
    });
    return Array.from(byId.values())
      .sort((a, b) => a.order - b.order)
      .map((screen, index) => ({ ...screen, order: index + 1 }));
  },

  getStatusUpdateScreens: async (orgID: number) => {
    const cacheBuster = Date.now();
    const url = buildApiUrl(`/api/v1/settings/status-update-screens?orgID=${orgID}&_ts=${cacheBuster}`);
    const response = await apiFetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const screens = settingsAPI.normalizeStatusUpdateScreens(data?.screens ?? data?.value);
    return { ...data, screens };
  },

  setStatusUpdateScreens: async (orgID: number, screens: Array<{ id: string; label: string; enabled: boolean; order: number }>) => {
    const response = await apiFetch(buildApiUrl('/api/v1/settings/status-update-screens'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID, screens }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return {
      ...data,
      screens: settingsAPI.normalizeStatusUpdateScreens(data?.screens ?? data?.value ?? screens),
    };
  },
};

// Audit Log API functions
export const auditLogAPI = {
  // Get audit log for an operation
  getAuditLog: async (opsID: number) => {
    console.log('🌐 API CALL: GET /api/v1/auditlog/' + opsID);
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.AUDITLOG(opsID)));
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Get audit log for a specific team
  getAuditLogForTeam: async (opsID: number, teamID: number) => {
    console.log('🌐 API CALL: GET /api/v1/auditlog/' + opsID + '/team/' + teamID);
    const response = await apiFetch(buildApiUrl(`/api/v1/auditlog/${opsID}/team/${teamID}`));
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Create audit log entry
  createAuditLog: async (logData: {
    opsID: number;
    teamID?: number;
    logType: string;
    message: string;
    shortDescription?: string;
    logData?: any;
  }) => {
    console.log('🌐 API CALL: POST /api/v1/auditlog', logData);
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.AUDITLOG_CREATE), {
      method: 'POST',
      body: JSON.stringify(logData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
};

export default {
  team: teamAPI,
  settings: settingsAPI,
  auditLog: auditLogAPI,
}; 